import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  PlugZap,
  RefreshCw,
  Receipt,
  Save,
  Send,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/affidya-fic`;
const DEFAULT_NUMERATION = "__default__";

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("super_admin_token")}` };
}

function errorMessage(json, fallback) {
  return typeof json?.detail === "string" ? json.detail : fallback;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatEuro(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function numerationLabel(sezionale) {
  if (!sezionale) return "(predefinita)";
  return sezionale.label || sezionale.numeration || "(predefinita)";
}

function documentoNumero(invoice) {
  if (invoice?.numero == null) return "—";
  const sez = invoice.numeration ? `/${invoice.numeration}` : "";
  const anno = invoice.anno ? `/${invoice.anno}` : "";
  return `${invoice.numero}${sez}${anno}`;
}

const emptyForm = {
  api_key: "",
  api_uid: "",
  numeration: "",
  vat_id: "",
  prezzi_ivati: true,
  e_invoice: true,
  invia_sdi: false,
  auto_emit: false,
  abilitato: false,
  descrizione_template: "Abbonamento Affidya {plan_label} ({period}) — {organizationName}",
};

export default function AffidyaFicAdminModule() {
  const [tab, setTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [emittingId, setEmittingId] = useState(null);
  const [sendingSdiId, setSendingSdiId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({ company_name: "", sezionali: [], vat_types: [], last_sync_at: null, updated_at: null });
  const [pending, setPending] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const loadConfig = useCallback(async () => {
    const res = await fetch(`${BASE}/config`, { headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(errorMessage(data, "Impossibile caricare la configurazione FIC"));
    setForm({
      api_key: data.api_key || "",
      api_uid: data.api_uid || "",
      numeration: data.numeration ?? "",
      vat_id: data.vat_id != null ? String(data.vat_id) : "",
      prezzi_ivati: data.prezzi_ivati !== false,
      e_invoice: data.e_invoice !== false,
      invia_sdi: !!data.invia_sdi,
      auto_emit: !!data.auto_emit,
      abilitato: !!data.abilitato,
      descrizione_template: data.descrizione_template || emptyForm.descrizione_template,
    });
    setMeta({
      company_name: data.company_name || "",
      sezionali: data.sezionali || [],
      vat_types: data.vat_types || [],
      last_sync_at: data.last_sync_at || null,
      updated_at: data.updated_at || null,
    });
  }, []);

  const loadLists = useCallback(async () => {
    setLoadingLists(true);
    try {
      const [pendingRes, invoicesRes] = await Promise.all([
        fetch(`${BASE}/pending`, { headers: authHeaders() }),
        fetch(`${BASE}/invoices`, { headers: authHeaders() }),
      ]);
      const pendingJson = await pendingRes.json().catch(() => ({}));
      const invoicesJson = await invoicesRes.json().catch(() => ({}));
      if (pendingRes.ok) setPending(pendingJson.items || []);
      if (invoicesRes.ok) setInvoices(invoicesJson.items || []);
    } finally {
      setLoadingLists(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadConfig();
        if (!cancelled) await loadLists();
      } catch (e) {
        if (!cancelled) toast.error(e.message || "Errore di caricamento");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConfig, loadLists]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.api_uid || !form.api_key) {
      toast.error("Company ID e token API sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/config`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          vat_id: form.vat_id === "" ? null : Number(form.vat_id),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, "Errore salvataggio"));
      toast.success("Configurazione FIC salvata");
      await loadConfig();
    } catch (err) {
      toast.error(err.message || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${BASE}/test-connection`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, "Connessione FIC non riuscita"));
      toast.success(data.message || "Connessione riuscita");
      setMeta((prev) => ({
        ...prev,
        company_name: data.company_name || prev.company_name,
        sezionali: data.sezionali || [],
        vat_types: data.vat_types || [],
        last_sync_at: new Date().toISOString(),
      }));
    } catch (err) {
      toast.error(err.message || "Errore di connessione");
    } finally {
      setTesting(false);
    }
  };

  const handleEmit = async (item) => {
    const key = item.payment_transaction_id || item.organization_id;
    setEmittingId(key);
    try {
      const res = await fetch(`${BASE}/invoices`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          organization_id: item.organization_id,
          payment_transaction_id: item.payment_transaction_id || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, "Emissione non riuscita"));
      const numero = documentoNumero(data.invoice);
      toast.success(
        data.already_exists
          ? `Fattura già presente su FIC: ${numero}`
          : `Fattura emessa con numerazione FIC ${numero}`
      );
      if (data.sdi_warning) toast.warning(data.sdi_warning);
      await loadLists();
    } catch (err) {
      toast.error(err.message || "Errore emissione fattura");
    } finally {
      setEmittingId(null);
    }
  };

  const handleSendSdi = async (invoice) => {
    setSendingSdiId(invoice.id);
    try {
      const res = await fetch(`${BASE}/invoices/${invoice.id}/send-sdi`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, "Invio SDI non riuscito"));
      toast.success(data.already_sent ? "Già inviata allo SDI" : "Fattura inviata allo SDI");
      await loadLists();
    } catch (err) {
      toast.error(err.message || "Errore invio SDI");
    } finally {
      setSendingSdiId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const selectedSezionale = (meta.sezionali || []).find(
    (s) => (s.numeration || "") === (form.numeration || "")
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
          <Receipt className="h-5 w-5 text-teal-600" />
          Fatturazione abbonamenti (Fatture in Cloud)
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Configura la società FIC di Affidya per emettere le fatture dei pagamenti Stripe e dei bonifici.
          Il numero documento è assegnato da Fatture in Cloud sul sezionale attivo, senza un contatore locale.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="config">Configurazione</TabsTrigger>
          <TabsTrigger value="pending">
            Da fatturare
            {pending.length > 0 && (
              <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">{pending.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invoices">Fatture emesse</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Connessione Fatture in Cloud</CardTitle>
              <p className="text-sm text-slate-500">
                Usa il token API e il Company ID della società Affidya (non quello delle RSA).
                {meta.company_name && (
                  <span className="ml-1 font-medium text-slate-700">Società: {meta.company_name}</span>
                )}
                {meta.last_sync_at && (
                  <span className="ml-2 text-xs text-slate-400">
                    Sezionali aggiornati: {formatDate(meta.last_sync_at)}
                  </span>
                )}
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="api_uid">
                      Company ID <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="api_uid"
                      value={form.api_uid}
                      onChange={(e) => setField("api_uid", e.target.value)}
                      placeholder="es. 1234567"
                      className="font-mono"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="api_key">
                      Token API <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="api_key"
                      type="password"
                      autoComplete="off"
                      value={form.api_key}
                      onChange={(e) => setField("api_key", e.target.value)}
                      placeholder="Bearer token FIC"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={handleTest} disabled={testing}>
                    {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
                    Testa connessione e sezionali
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label>Sezionale FIC (numerazione attiva)</Label>
                    <Select
                      value={form.numeration === "" ? DEFAULT_NUMERATION : form.numeration}
                      onValueChange={(v) => setField("numeration", v === DEFAULT_NUMERATION ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona sezionale" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DEFAULT_NUMERATION}>
                          {(meta.sezionali || []).find((s) => s.numeration === "")
                            ? `(predefinita) — prossimo ${(meta.sezionali || []).find((s) => s.numeration === "").prossimo_numero}`
                            : "(predefinita su FIC)"}
                        </SelectItem>
                        {(meta.sezionali || [])
                          .filter((s) => s.numeration !== "")
                          .map((s) => (
                            <SelectItem key={s.numeration} value={s.numeration}>
                              {numerationLabel(s)} — prossimo {s.prossimo_numero}/{s.anno}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {selectedSezionale && (
                      <p className="mt-1 text-xs text-slate-500">
                        Prossimo numero su FIC: <strong>{selectedSezionale.prossimo_numero}</strong> / {selectedSezionale.anno}
                        {selectedSezionale.ultimo_numero ? ` (ultimo emesso: ${selectedSezionale.ultimo_numero})` : ""}
                      </p>
                    )}
                    {!selectedSezionale && (
                      <p className="mt-1 text-xs text-slate-400">
                        Il numero non viene deciso da Affidya: FIC assegna il progressivo del sezionale scelto.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Aliquota IVA FIC</Label>
                    <Select
                      value={form.vat_id === "" ? "none" : form.vat_id}
                      onValueChange={(v) => setField("vat_id", v === "none" ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona aliquota" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non impostata</SelectItem>
                        {(meta.vat_types || []).map((vt) => (
                          <SelectItem key={vt.id} value={String(vt.id)}>
                            {vt.value != null ? `${vt.value}%` : "IVA"} {vt.description ? `— ${vt.description}` : ""}
                            {vt.is_default ? " (predefinita)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="descrizione_template">Descrizione voce in fattura</Label>
                  <Textarea
                    id="descrizione_template"
                    value={form.descrizione_template}
                    onChange={(e) => setField("descrizione_template", e.target.value)}
                    rows={2}
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    Variabili: <code className="rounded bg-slate-100 px-1">{"{organizationName}"}</code>,{" "}
                    <code className="rounded bg-slate-100 px-1">{"{plan_label}"}</code>,{" "}
                    <code className="rounded bg-slate-100 px-1">{"{period}"}</code>
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                    <span className="text-sm">Prezzi già IVA inclusa (listino Affidya)</span>
                    <Switch checked={form.prezzi_ivati} onCheckedChange={(v) => setField("prezzi_ivati", v)} />
                  </label>
                  <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                    <span className="text-sm">Fattura elettronica (SDI)</span>
                    <Switch checked={form.e_invoice} onCheckedChange={(v) => setField("e_invoice", v)} />
                  </label>
                  <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                    <span className="text-sm">Invia automaticamente allo SDI dopo l&apos;emissione</span>
                    <Switch checked={form.invia_sdi} onCheckedChange={(v) => setField("invia_sdi", v)} disabled={!form.e_invoice} />
                  </label>
                  <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                    <span className="text-sm">Emetti in automatico a pagamento completato</span>
                    <Switch checked={form.auto_emit} onCheckedChange={(v) => setField("auto_emit", v)} />
                  </label>
                </div>

                <label className="flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Abilita fatturazione abbonamenti</p>
                    <p className="text-xs text-slate-500">Senza questo flag non vengono create fatture su FIC.</p>
                  </div>
                  <Switch checked={form.abilitato} onCheckedChange={(v) => setField("abilitato", v)} />
                </label>

                <div className="flex justify-end">
                  <Button type="submit" disabled={saving}>
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Salva configurazione
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Pagamenti da fatturare</CardTitle>
                <p className="mt-1 text-sm text-slate-500">
                  Pagamenti completati (Stripe o bonifico) senza una fattura Affidya su FIC.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={loadLists} disabled={loadingLists}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loadingLists ? "animate-spin" : ""}`} />
                Aggiorna
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {pending.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-slate-500">Nessun pagamento in attesa di fattura.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-2">Cliente</th>
                        <th className="px-4 py-2">Piano</th>
                        <th className="px-4 py-2">Importo</th>
                        <th className="px-4 py-2">Pagamento</th>
                        <th className="px-4 py-2">Dati fiscali</th>
                        <th className="px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map((item) => {
                        const key = item.payment_transaction_id || item.organization_id;
                        return (
                          <tr key={key} className="border-t border-slate-100">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800">{item.organization_name || "—"}</div>
                              <div className="text-xs text-slate-500">{item.admin_email || ""}</div>
                            </td>
                            <td className="px-4 py-3">
                              {item.plan_label} · {item.period}
                            </td>
                            <td className="px-4 py-3 font-medium">{formatEuro(item.importo)}</td>
                            <td className="px-4 py-3">
                              <div>{item.payment_method === "bank_transfer" ? "Bonifico" : "Stripe"}</div>
                              <div className="text-xs text-slate-500">{formatDate(item.completed_at)}</div>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600">
                              {item.partita_iva ? `P.IVA ${item.partita_iva}` : "P.IVA mancante"}
                              {item.sdi ? ` · SDI ${item.sdi}` : ""}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button size="sm" onClick={() => handleEmit(item)} disabled={emittingId === key || !form.abilitato}>
                                {emittingId === key ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Receipt className="mr-2 h-4 w-4" />
                                )}
                                Emetti su FIC
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Fatture emesse su FIC</CardTitle>
                <p className="mt-1 text-sm text-slate-500">Numerazione riportata da Fatture in Cloud dopo la creazione.</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadLists} disabled={loadingLists}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loadingLists ? "animate-spin" : ""}`} />
                Aggiorna
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {invoices.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-slate-500">Nessuna fattura abbonamento emessa.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-2">Numero FIC</th>
                        <th className="px-4 py-2">Cliente</th>
                        <th className="px-4 py-2">Importo</th>
                        <th className="px-4 py-2">Stato</th>
                        <th className="px-4 py-2">Data</th>
                        <th className="px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-t border-slate-100">
                          <td className="px-4 py-3 font-mono text-sm font-medium">{documentoNumero(inv)}</td>
                          <td className="px-4 py-3">{inv.organization_name || "—"}</td>
                          <td className="px-4 py-3">{formatEuro(inv.importo)}</td>
                          <td className="px-4 py-3">
                            {inv.sdi_inviata ? (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                <CheckCircle2 className="mr-1 h-3 w-3" />
                                SDI
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Emessa su FIC</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{formatDate(inv.created_at)}</td>
                          <td className="px-4 py-3 text-right">
                            {inv.url && (
                              <Button asChild size="sm" variant="ghost">
                                <a href={inv.url} target="_blank" rel="noreferrer">
                                  PDF
                                </a>
                              </Button>
                            )}
                            {!inv.sdi_inviata && form.e_invoice && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendSdi(inv)}
                                disabled={sendingSdiId === inv.id}
                              >
                                {sendingSdiId === inv.id ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="mr-2 h-4 w-4" />
                                )}
                                Invia SDI
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
